import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraInput, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EvidenceAsk } from '../../../components/EvidenceAsk/EvidenceAsk';
import { portfolioClient } from '../../../lib/connect/clients';
import { EconomicsFigures } from './EconomicsFigures';
function failureKind(error) {
    if (!(error instanceof ConnectError)) return 'failed';
    if (error.code === Code.PermissionDenied) return 'denied';
    if (error.code === Code.Unavailable && /not configured/i.test(error.rawMessage)) {
        return 'unconfigured';
    }
    return 'failed';
}
/**
 * LAND-08: reviewable AI synthesis over evidence the viewer may already read.
 * Totals are the server's projection, never the model's. Nothing is stored
 * until the viewer accepts a retain_observation proposal.
 */ export function LandscapeSynthesisPanel({ landscapeId, landscapeName }) {
    const [question, setQuestion] = useState('');
    const [decisions, setDecisions] = useState({});
    const [retainFailed, setRetainFailed] = useState(false);
    const synthesis = useMutation({
        mutationFn: (q)=>portfolioClient.getLandscapeSynthesis({
                landscapeId,
                question: q
            }),
        onMutate: ()=>{
            setDecisions({});
            setRetainFailed(false);
        }
    });
    const retain = useMutation({
        mutationFn: (p)=>portfolioClient.retainLandscapeObservation({
                landscapeId,
                text: p.payload.text ?? '',
                lineage: p.lineage
            })
    });
    const data = synthesis.data;
    function accept(index, proposal) {
        setRetainFailed(false);
        retain.mutate(proposal, {
            onSuccess: ()=>setDecisions((d)=>({
                        ...d,
                        [index]: 'retained'
                    })),
            onError: ()=>setRetainFailed(true)
        });
    }
    const ask = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("form", {
                className: "flex items-center gap-2",
                onSubmit: (e)=>{
                    e.preventDefault();
                    synthesis.mutate(question.trim());
                },
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Question about this Landscape",
                        className: "flex-1",
                        placeholder: "What changed, and what should we research next?",
                        value: question,
                        onChange: (e)=>setQuestion(e.target.value),
                        "data-testid": Testids.landscapes.synthesisQuestion
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        type: "submit",
                        disabled: synthesis.isPending,
                        "data-testid": Testids.landscapes.synthesisAsk,
                        children: synthesis.isPending ? 'Working…' : 'Summarize'
                    })
                ]
            }),
            synthesis.isError && /*#__PURE__*/ _jsx(SynthesisFailure, {
                error: synthesis.error
            }),
            data && /*#__PURE__*/ _jsx(SynthesisResult, {
                data: data,
                decisions: decisions,
                retaining: retain.isPending,
                retainFailed: retainFailed,
                onAccept: accept,
                onReject: (i)=>setDecisions((d)=>({
                            ...d,
                            [i]: 'rejected'
                        }))
            })
        ]
    });
    return /*#__PURE__*/ _jsx("section", {
        "data-testid": Testids.landscapes.synthesisPanel,
        "aria-label": "AI synthesis",
        children: /*#__PURE__*/ _jsx(EvidenceAsk, {
            context: {
                workspace: 'Landscape',
                record: {
                    id: landscapeId,
                    label: landscapeName || 'Current Landscape'
                },
                version: 'Live evidence you are permitted to read',
                filters: ''
            },
            mode: "ask",
            evidence: /*#__PURE__*/ _jsx("p", {
                children: "The AI reads only the Landscape evidence you can already read."
            }),
            ask: ask
        })
    });
}
function SynthesisFailure({ error }) {
    const kind = failureKind(error);
    if (kind === 'denied') {
        return /*#__PURE__*/ _jsx("p", {
            role: "alert",
            "data-testid": Testids.landscapes.synthesisDenied,
            children: "You do not have permission to use AI synthesis for this Landscape."
        });
    }
    if (kind === 'unconfigured') {
        return /*#__PURE__*/ _jsx("p", {
            role: "alert",
            "data-testid": Testids.landscapes.synthesisUnconfigured,
            children: "AI synthesis is not configured for this environment."
        });
    }
    return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        "data-testid": Testids.landscapes.synthesisError,
        children: "AI synthesis could not be completed. Try again later."
    });
}
function SynthesisResult({ data, decisions, retaining, retainFailed, onAccept, onReject }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "whitespace-pre-wrap text-sm",
                "data-testid": Testids.landscapes.synthesisAnswer,
                children: data.answer
            }),
            data.caveats.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "rounded-md border border-border p-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "What this summary does not cover"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc pl-5",
                        children: data.caveats.map((c)=>/*#__PURE__*/ _jsx("li", {
                                "data-testid": Testids.landscapes.synthesisCaveat,
                                "data-category": c.category,
                                children: c.message
                            }, c.category))
                    })
                ]
            }),
            data.sources.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "text-sm",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "Sources used"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc pl-5",
                        children: data.sources.map((s)=>/*#__PURE__*/ _jsx("li", {
                                "data-testid": Testids.landscapes.synthesisCitation,
                                "data-source-id": s.id,
                                children: s.label
                            }, `${s.kind}:${s.id}`))
                    })
                ]
            }),
            data.totals && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.landscapes.synthesisTotals,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "mb-1 text-sm font-medium",
                        children: "Totals (calculated from your records)"
                    }),
                    /*#__PURE__*/ _jsx(EconomicsFigures, {
                        figures: data.totals
                    })
                ]
            }),
            retainFailed && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                className: "text-sm",
                children: "The observation could not be saved. It was not retained."
            }),
            data.proposals.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                "aria-label": "AI proposals",
                children: data.proposals.map((p, i)=>/*#__PURE__*/ _jsx(ProposalRow, {
                        proposal: p,
                        decision: decisions[i],
                        busy: retaining,
                        onAccept: ()=>onAccept(i, p),
                        onReject: ()=>onReject(i)
                    }, `${p.kind}:${p.title}:${i}`))
            })
        ]
    });
}
function ProposalRow({ proposal, decision, busy, onAccept, onReject }) {
    const outline = specraButtonVariants({
        variant: 'outline',
        size: 'sm'
    });
    let accept;
    if (proposal.kind === 'create_profile') {
        accept = /*#__PURE__*/ _jsx(Link, {
            className: outline,
            to: "/opportunities/tracking-profiles/create",
            "data-testid": Testids.landscapes.synthesisAccept,
            children: "Accept: create Profile"
        });
    } else if (proposal.kind === 'open_record') {
        accept = /*#__PURE__*/ _jsx(Link, {
            className: outline,
            to: `/opportunities?watchOpportunity=${encodeURIComponent(proposal.payload.opportunity_id ?? '')}`,
            "data-testid": Testids.landscapes.synthesisAccept,
            children: "Accept: open record"
        });
    } else {
        accept = /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            disabled: busy,
            onClick: onAccept,
            "data-testid": Testids.landscapes.synthesisAccept,
            children: "Accept: retain observation"
        });
    }
    return /*#__PURE__*/ _jsxs("li", {
        className: "rounded-md border border-border p-2 text-sm",
        "data-testid": Testids.landscapes.synthesisProposal,
        "data-kind": proposal.kind,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "font-medium",
                children: proposal.title
            }),
            proposal.kind === 'retain_observation' && /*#__PURE__*/ _jsx("p", {
                className: "whitespace-pre-wrap",
                children: proposal.payload.text
            }),
            proposal.kind === 'create_profile' && /*#__PURE__*/ _jsx("p", {
                children: proposal.payload.name
            }),
            decision === 'retained' && /*#__PURE__*/ _jsx("output", {
                "data-testid": Testids.landscapes.synthesisRetained,
                children: "Observation retained."
            }),
            decision === 'rejected' && /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Rejected."
            }),
            !decision && /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex gap-2",
                children: [
                    accept,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: onReject,
                        "data-testid": Testids.landscapes.synthesisReject,
                        children: "Reject"
                    })
                ]
            })
        ]
    });
}
